import { SR_DESIGN_HEADER, TENANT_HEADER } from '@tvmf/shared';

let accessToken: string | null = null;
let activeTenantId: string | null = null;
let onAuthLost: (() => void) | null = null;
/**
 * Set while a service request's Design tab is open: Design & Build rows
 * created there are linked to that request (see ServiceRequestLinkInterceptor).
 * Only sent on POSTs to Design & Build.
 */
let designingRequestId: string | null = null;

export function setDesigningServiceRequest(id: string | null) {
  designingRequestId = id;
}

export const auth = {
  setToken(t: string | null) {
    accessToken = t;
  },
  getToken() {
    return accessToken;
  },
  setActiveTenant(id: string | null) {
    activeTenantId = id;
  },
  getActiveTenant() {
    return activeTenantId;
  },
  onLost(cb: () => void) {
    onAuthLost = cb;
  },
};

/**
 * The API's body validation answers `{message: 'Validation failed', issues: [{path, message}]}`. On its own that
 * says nothing, so name the fields that failed: "Validation failed - name: Too small; site_id: Invalid GUID".
 */
function withIssues(message: string, body: unknown): string {
  const issues = body && typeof body === 'object' ? (body as { issues?: unknown }).issues : undefined;
  if (!Array.isArray(issues) || !issues.length) return message;
  const parts = issues
    .map((i) => {
      const { path, message: m } = (i ?? {}) as { path?: unknown; message?: unknown };
      const text = typeof m === 'string' ? m : '';
      return path ? `${String(path)}: ${text}` : text;
    })
    .filter(Boolean);
  return parts.length ? `${message} - ${parts.join('; ')}` : message;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

async function raw(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (activeTenantId) headers.set(TENANT_HEADER, activeTenantId);
  if (designingRequestId && init.method === 'POST' && /\/t\/[^/]+\/build\//.test(path)) headers.set(SR_DESIGN_HEADER, designingRequestId);
  // FormData sets its own multipart boundary in the Content-Type header the
  // browser generates - stamping application/json here would break it.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(path.startsWith('/') ? path : `/${path}`, { ...init, headers, credentials: 'include' });
}

/** Attempt a silent refresh using the HttpOnly cookie. Returns a new access token or null. */
export async function tryRefresh(): Promise<string | null> {
  const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
  if (!res.ok) return null;
  const data = (await res.json()) as { accessToken: string };
  accessToken = data.accessToken;
  return data.accessToken;
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await raw(`/api${path}`, init);
  if (res.status === 401 && accessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await raw(`/api${path}`, init);
    } else {
      accessToken = null;
      onAuthLost?.();
    }
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const msg =
      (body && (body.message || body.error)) || `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, withIssues(Array.isArray(msg) ? msg.join(', ') : String(msg), body), body);
  }
  return body as T;
}

/** Upload a single file as multipart/form-data (field name "file"), e.g. a number-port document. */
export async function apiUpload<T = unknown>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  return api<T>(path, { method: 'POST', body: form });
}

/**
 * Download a binary response (a file's `.../download` route) and save it
 * with the browser's normal download UI. A plain `<a href>` can't do this -
 * every API route needs the bearer token in an Authorization header
 * (auth.getToken(), see raw() above), which a browser-navigated link never
 * sends - so this fetches like api() does but as a Blob, then triggers the
 * save via a synthetic anchor with a client-side object URL.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  let res = await raw(`/api${path}`, {});
  if (res.status === 401 && accessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) res = await raw(`/api${path}`, {});
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = JSON.parse(await res.text());
      msg = body?.message || body?.error || msg;
    } catch {
      /* not JSON - keep the status text */
    }
    throw new ApiError(res.status, Array.isArray(msg) ? msg.join(', ') : String(msg));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
